import { Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { getAppDateFormatPlaceholder } from '@shared/common/pipes/format.util';
import { Table } from 'primeng/table';
import {
    formatDateForServerFilter,
    parseDisplayDate,
    TABLE_DATE_CONTAINS_MATCH_MODE,
    TABLE_DATE_EQUALS_MATCH_MODE,
} from './table-filter.util';

@Component({
    standalone: false,
    selector: 'app-table-column-date-filter',
    templateUrl: './table-column-date-filter.component.html',
    styleUrls: ['./table-column-date-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TableColumnDateFilterComponent {
    @ViewChild('dp') datepicker: BsDatepickerDirective;
    @Input() table: Table;
    @Input() field: string;
    @Input() placeholder: string;
    @Input() dateString: string;
    @Input() filterMode: 'dateEquals' | 'containsFormatted' | 'serverEqual' = 'dateEquals';
    @Output() dateStringChange = new EventEmitter<string>();
    @Output() dateChange = new EventEmitter<Date | null>();

    selectedDate: Date;
    displayValue = '';
    private pickerInitialized = false;

    get datePlaceholder(): string {
        return this.placeholder || getAppDateFormatPlaceholder();
    }

    get bsConfig() {
        return {
            showWeekNumbers: false,
            adaptivePosition: true,
            dateInputFormat: this.datePlaceholder,
            containerClass: 'theme-default',
        };
    }

    togglePicker(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.datepicker) {
            return;
        }
        if (this.datepicker.isOpen) {
            this.datepicker.hide();
            return;
        }
        this.datepicker.show();
    }

    onPickerDateChange(date: Date | null): void {
        if (!this.pickerInitialized) {
            this.pickerInitialized = true;
            if (!date) {
                return;
            }
        }
        if (date && isNaN(date.getTime())) {
            return;
        }
        this.selectedDate = date;
        this.displayValue = date ? this.formatSelectedDate(date) : '';
        this.applyInstantFilter(this.displayValue, date);
    }

    onDisplayValueChange(value: string): void {
        this.displayValue = value ?? '';
        const raw = this.displayValue.trim();
        if (!raw) {
            this.selectedDate = null;
            this.applyInstantFilter('', null);
            return;
        }

        const parsed = parseDisplayDate(raw, this.datePlaceholder);
        if (parsed) {
            this.selectedDate = parsed;
        }
        this.applyInstantFilter(raw, parsed);
    }

    onDisplayBlur(): void {
        const raw = this.displayValue.trim();
        if (!raw) {
            return;
        }

        const parsed = parseDisplayDate(raw, this.datePlaceholder, { allowTwoDigitYear: true });
        if (!parsed) {
            return;
        }

        this.selectedDate = parsed;
        this.displayValue = this.formatSelectedDate(parsed);
        this.applyInstantFilter(this.displayValue, parsed);
    }

    private applyInstantFilter(raw: string, parsed: Date | null): void {
        if (!raw) {
            this.emitAndFilter(null, '');
            return;
        }

        if (this.filterMode === 'serverEqual') {
            if (!raw) {
                this.emitAndFilter(null, '');
            } else if (parsed) {
                this.emitAndFilter(parsed, formatDateForServerFilter(parsed));
            }
            return;
        }

        if (this.filterMode === 'containsFormatted') {
            this.applyClientSideFilter(raw, 'contains');
            return;
        }

        this.applyClientSideFilter(raw, TABLE_DATE_CONTAINS_MATCH_MODE);
    }

    private applyClientSideFilter(value: string, matchMode: string): void {
        if (!this.table || !this.field) {
            return;
        }
        this.table.filter(value, this.field, matchMode);
    }

    private emitAndFilter(date: Date | null, formattedForServer: string): void {
        this.dateStringChange.emit(formattedForServer);

        if (!this.table || !this.field) {
            this.dateChange.emit(date);
            return;
        }

        if (!date) {
            const clearMode = this.filterMode === 'containsFormatted'
                ? 'contains'
                : TABLE_DATE_CONTAINS_MATCH_MODE;
            this.table.filter(null, this.field, clearMode);
            return;
        }

        if (this.filterMode === 'containsFormatted') {
            this.table.filter(this.formatSelectedDate(date), this.field, 'contains');
            return;
        }

        this.table.filter(date, this.field, TABLE_DATE_EQUALS_MATCH_MODE);
    }

    private formatSelectedDate(date: Date): string {
        const placeholder = this.datePlaceholder;
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return placeholder
            .replace('DD', day)
            .replace('MM', month)
            .replace('YYYY', year);
    }
}
